import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import api from "../api/client";

const STUDENT_TOUR_STEPS = [
  {
    target: null,
    title: "Bienvenue sur Odys'Mentoring !",
    description: "Laissez-moi vous faire découvrir votre espace en quelques secondes.",
    position: "center"
  },
  {
    target: '[data-testid="student-tab-parcours"]',
    title: "Votre Parcours",
    description: "Suivez ici votre progression à travers les 11 étapes clés de l'investissement immobilier. Votre coach valide chaque étape avec vous.",
    position: "bottom"
  },
  {
    target: '[data-testid="student-tab-odysboard"]',
    title: "Connectez votre Odysboard !",
    description: "C'est important ! Liez votre compte Odysboard pour synchroniser vos données et permettre à votre coach de suivre votre avancement en temps réel.",
    position: "bottom",
    highlight: true,
    pulse: true
  },
  {
    target: '[data-testid="book-rdv-btn"]',
    title: "Prenez RDV avec votre Major !",
    description: "Quand votre coach aura configuré son calendrier, vous pourrez réserver un créneau ici. En attendant, contactez-le via la messagerie !",
    position: "top",
    highlight: true
  },
  {
    target: '[data-tour="sidebar-messages"]',
    title: "Messagerie",
    description: "Échangez directement avec votre coach ici. N'hésitez pas à poser toutes vos questions, il est là pour vous accompagner !",
    position: "right"
  },
  {
    target: '[data-testid="notification-bell"]',
    title: "Notifications",
    description: "Restez informé des messages de votre coach et des échéances importantes. Consultez-les régulièrement !",
    position: "bottom"
  },
  {
    target: null,
    title: "C'est parti !",
    description: "Vous êtes prêt ! Deux actions prioritaires : connectez votre Odysboard et prenez RDV avec votre Major. Bon investissement !",
    position: "center",
    cta: true
  }
];

const COACH_TOUR_STEPS = [
  {
    target: null,
    title: "Bienvenue Coach !",
    description: "Découvrez votre espace de suivi pour accompagner vos élèves vers leur premier investissement.",
    position: "center"
  },
  {
    target: '[data-testid="coach-tab-students"]',
    title: "Vos Élèves",
    description: "Suivez la progression de chaque élève. Les scores de risque et la barre de progression vous aident à identifier qui a besoin d'aide.",
    position: "bottom",
    highlight: true
  },
  {
    target: '[data-tour="sidebar-messages"]',
    title: "Messagerie",
    description: "Communiquez avec vos élèves. Répondez rapidement pour maintenir leur motivation !",
    position: "right"
  },
  {
    target: '[data-testid="notification-bell"]',
    title: "Notifications",
    description: "Ne manquez aucun message important de vos élèves.",
    position: "bottom"
  },
  {
    target: null,
    title: "À vous de jouer !",
    description: "Vos élèves comptent sur vous. Accompagnez-les vers la réussite de leur investissement immobilier !",
    position: "center",
    cta: true
  }
];

export default function GuidedTour({ role, onComplete }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  
  const steps = role === "coach" ? COACH_TOUR_STEPS : STUDENT_TOUR_STEPS;
  const step = steps[currentStep];
  const isLast = currentStep === steps.length - 1;

  // Find target element and get its position
  useEffect(() => {
    const findTarget = () => {
      if (!step.target) {
        setTargetRect(null);
        return;
      }
      
      const element = document.querySelector(step.target);
      if (element) {
        const rect = element.getBoundingClientRect();
        setTargetRect({
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height
        });
      } else {
        setTargetRect(null);
      }
    };

    findTarget();
    window.addEventListener("resize", findTarget);
    window.addEventListener("scroll", findTarget);
    
    // Retry after a delay for dynamic elements
    const timeout = setTimeout(findTarget, 300);
    
    return () => {
      window.removeEventListener("resize", findTarget);
      window.removeEventListener("scroll", findTarget);
      clearTimeout(timeout);
    };
  }, [currentStep, step.target]);

  const handleNext = () => {
    if (isLast) {
      api.post("/auth/tutorial-complete").catch(() => {});
      onComplete();
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleSkip = () => {
    api.post("/auth/tutorial-complete").catch(() => {});
    onComplete();
  };

  // Tooltip positioning
  const getTooltipStyle = () => {
    if (!targetRect || step.position === "center") {
      return {
        position: "fixed",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        zIndex: 110
      };
    }

    const padding = 20;
    let top, left;

    switch (step.position) {
      case "bottom":
        top = targetRect.top + targetRect.height + padding;
        left = targetRect.left + (targetRect.width / 2) - 160;
        break;
      case "top":
        top = targetRect.top - padding - 200;
        left = targetRect.left + (targetRect.width / 2) - 160;
        break;
      case "right":
        top = targetRect.top + (targetRect.height / 2) - 100;
        left = targetRect.left + targetRect.width + padding;
        break;
      case "left":
        top = targetRect.top + (targetRect.height / 2) - 100;
        left = targetRect.left - padding - 320;
        break;
      default:
        top = targetRect.top + targetRect.height + padding;
        left = targetRect.left;
    }

    // Keep within viewport
    top = Math.max(20, Math.min(top, window.innerHeight - 250));
    left = Math.max(20, Math.min(left, window.innerWidth - 340));

    return {
      position: "fixed",
      top: `${top}px`,
      left: `${left}px`,
      zIndex: 110
    };
  };

  // Dark overlay color
  const overlayColor = "rgba(5, 10, 20, 0.92)";

  // Inject a global style to ensure the overlay covers everything
  useEffect(() => {
    const styleId = 'guided-tour-overlay-style';
    let style = document.getElementById(styleId);
    if (!style) {
      style = document.createElement('style');
      style.id = styleId;
      document.head.appendChild(style);
    }
    // Disable transforms on all elements to break stacking contexts
    style.textContent = `
      .guided-tour-overlay {
        position: fixed !important;
        top: 0 !important;
        left: 0 !important;
        right: 0 !important;
        bottom: 0 !important;
        width: 100% !important;
        height: 100% !important;
        background: ${overlayColor} !important;
        z-index: 2147483646 !important;
        pointer-events: none !important;
      }
      /* Break stacking contexts during tour */
      body.guided-tour-active * {
        transform: none !important;
        will-change: auto !important;
        filter: none !important;
        perspective: none !important;
      }
      body.guided-tour-active .sidebar-gradient,
      body.guided-tour-active [class*="translate"],
      body.guided-tour-active [class*="transform"] {
        transform: none !important;
      }
    `;
    
    // Add class to body
    document.body.classList.add('guided-tour-active');
    
    return () => {
      document.body.classList.remove('guided-tour-active');
      if (style && style.parentNode) {
        style.parentNode.removeChild(style);
      }
    };
  }, []);

  const tourContent = (
    <div style={{ position: 'fixed', inset: 0, zIndex: 2147483647 }} data-testid="guided-tour">
      {/* Full screen dark overlay - inline style for reliability */}
      <div 
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100vw',
          height: '100vh',
          background: overlayColor,
          zIndex: 2147483646,
          pointerEvents: 'none'
        }}
      />
      
      {/* Spotlight hole - positioned on top of overlay */}
      {targetRect && (
        <>
          {/* White rectangle to "reveal" the element beneath */}
          <div
            style={{
              position: 'fixed',
              top: targetRect.top - 12,
              left: targetRect.left - 12,
              width: targetRect.width + 24,
              height: targetRect.height + 24,
              borderRadius: "16px",
              backgroundColor: "#f8fafc",
              zIndex: 2147483647,
              pointerEvents: "none",
              transition: "all 0.3s ease"
            }}
          />
          {/* Border around the spotlight */}
          <div
            style={{
              position: 'fixed',
              top: targetRect.top - 12,
              left: targetRect.left - 12,
              width: targetRect.width + 24,
              height: targetRect.height + 24,
              borderRadius: "16px",
              border: step.highlight ? "3px solid #c9a227" : "3px solid rgba(255, 255, 255, 0.9)",
              boxShadow: step.highlight 
                ? "0 0 30px rgba(201, 162, 39, 0.5)"
                : "0 0 25px rgba(255, 255, 255, 0.3)",
              zIndex: 2147483647,
              pointerEvents: "none",
              transition: "all 0.3s ease"
            }}
          >
            {step.pulse && (
              <div 
                className="absolute inset-0 rounded-2xl animate-ping"
                style={{ 
                  border: "2px solid #c9a227",
                  opacity: 0.4,
                  animationDuration: "1.5s"
                }}
              />
            )}
          </div>
        </>
      )}

      {/* Tooltip */}
      <div
        className="bg-white rounded-2xl shadow-2xl w-80 overflow-hidden"
        style={{...getTooltipStyle(), zIndex: 2147483647}}
      >
        {/* Arrow pointing to target */}
        {targetRect && step.position !== "center" && (
          <div 
            className={`absolute w-4 h-4 bg-white transform rotate-45 ${
              step.position === "bottom" ? "-top-2 left-1/2 -translate-x-1/2 shadow-[-2px_-2px_4px_rgba(0,0,0,0.1)]" :
              step.position === "top" ? "-bottom-2 left-1/2 -translate-x-1/2 shadow-[2px_2px_4px_rgba(0,0,0,0.1)]" :
              step.position === "right" ? "-left-2 top-8 shadow-[-2px_2px_4px_rgba(0,0,0,0.1)]" :
              "-right-2 top-8 shadow-[2px_-2px_4px_rgba(0,0,0,0.1)]"
            }`}
          />
        )}

        {/* Progress bar */}
        <div className="h-1.5 bg-gray-100">
          <div 
            className="h-full bg-gradient-to-r from-[#1a2744] to-[#c9a227] transition-all duration-500"
            style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
          />
        </div>

        {/* Content */}
        <div className="p-5">
          {/* Step indicator */}
          <div className="flex items-center gap-2 mb-3">
            <span className="w-7 h-7 rounded-full bg-gradient-to-br from-[#1a2744] to-[#2d3e5f] text-white text-sm font-bold flex items-center justify-center">
              {currentStep + 1}
            </span>
            <span className="text-xs text-gray-400 font-medium">
              Étape {currentStep + 1}/{steps.length}
            </span>
          </div>

          {/* Title */}
          <h3 
            className="text-lg font-bold text-gray-900 mb-2"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            {step.title}
          </h3>

          {/* Description */}
          <p className="text-sm text-gray-600 leading-relaxed mb-4">
            {step.description}
          </p>

          {/* Step dots */}
          <div className="flex justify-center gap-1.5 mb-4">
            {steps.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentStep(i)}
                className={`h-2 rounded-full transition-all duration-300 ${
                  i === currentStep 
                    ? "bg-[#1a2744] w-5" 
                    : i < currentStep 
                      ? "bg-[#c9a227] w-2 hover:bg-[#b8922a]" 
                      : "bg-gray-200 w-2 hover:bg-gray-300"
                }`}
              />
            ))}
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            {!isLast && (
              <button
                onClick={handleSkip}
                className="flex-1 py-2.5 px-4 rounded-xl border border-gray-200 text-gray-600 text-sm font-medium hover:bg-gray-50 transition-colors"
                data-testid="tour-skip"
              >
                Passer
              </button>
            )}
            <button
              onClick={handleNext}
              className={`${isLast ? "w-full" : "flex-1"} py-2.5 px-4 rounded-xl text-sm font-semibold text-white transition-all
                ${step.cta 
                  ? "bg-gradient-to-r from-[#c9a227] to-[#d4af37] hover:from-[#b8922a] hover:to-[#c9a227]" 
                  : "bg-gradient-to-r from-[#1a2744] to-[#2d3e5f] hover:from-[#243454] hover:to-[#3d4e6f]"
                }
                shadow-lg hover:shadow-xl active:scale-[0.98]`}
              data-testid="tour-next"
            >
              {isLast ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  C'est parti !
                </span>
              ) : (
                "Suivant →"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Use portal to render outside of parent's stacking context
  return createPortal(tourContent, document.body);
}
